import Css from "./style.module.scss";

import * as Icons from "@phosphor-icons/react";
import { Button, Input, Modal } from "nlib/ui";
import { ModalFooter } from "nlib/ui/Modal";
import { getActiveOrganization } from "selectors/organizations";
import { getCancellationData } from "selectors/metaData";
import { getTextsData } from "selectors/texts";
import { useDispatch, useSelector } from "react-redux";
import { v4 as uuid } from "uuid";
import CancellationActions from "const/CancellationActions";
import CancellationOutcomes from "const/CancellationOutcomes";
import DateTimeFormats from "const/DateTimeFormats";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import StripeActions from "actions/StripeActions";
import Texts from "utils/Texts";
import moment from "moment";

const FEEDBACK_MAX_LENGTH = 2000;

const FEEDBACK_MIN_LENGTH = 20;

const FEEDBACK_MIN_WORDS = 3;

const FEEDBACK_WORD_PATTERN = /[a-zA-Z\u00C0-\u1FFF\u2800-\uFFFD]/;

const CancelSubscriptionModal = ({ onClose }) => {
  const dispatch = useDispatch();

  const activeOrganization = useSelector(getActiveOrganization);

  const cancellationData = useSelector(getCancellationData);

  const { uiTexts, messages, errors } = useSelector(getTextsData);

  const getCancellationContext = useCallback((data) => {
    if (!data?.retentionActive || !data.retentionDiscountEndsAt || moment(data.retentionDiscountEndsAt).isAfter()) {
      return data;
    }

    return { ...data, retentionActive: false, offerAvailable: false, offer: null };
  }, []);

  const checkResumeAvailable = useCallback((data) => {
    return !!(data?.resumeAvailable && data.subscriptionStatus !== "canceled" && data.cancellationScheduledAt
      && moment(data.cancellationScheduledAt).isAfter());
  }, []);

  const [context, setContext] = useState(() => getCancellationContext(cancellationData));

  const mountedRef = useRef(true);

  const submittingRef = useRef(false);

  const requestIdsRef = useRef({});

  const triggerRef = useRef(document.activeElement);

  const contentRef = useRef();

  const feedbackInputRef = useRef();

  const [pendingAction, setPendingAction] = useState(null);

  const [choosing, setChoosing] = useState(false);

  const [feedback, setFeedback] = useState("");

  const [feedbackRequestId, setFeedbackRequestId] = useState(null);

  const [error, setError] = useState(false);

  const [result, setResult] = useState(null);

  submittingRef.current = !!pendingAction;

  const hasFeedback = useMemo(() => {
    const text = feedback.trim();

    return text.length >= FEEDBACK_MIN_LENGTH
      && text.split(/\s+/).filter((word) => FEEDBACK_WORD_PATTERN.test(word)).length >= FEEDBACK_MIN_WORDS;
  }, [feedback]);

  const handleClose = useCallback(() => {
    if (!submittingRef.current) onClose();
  }, [onClose]);

  const handleModalShow = useCallback(() => {
    const dialog = contentRef.current?.closest("[role=dialog]");

    if (!dialog) return;
    if (document.activeElement?.closest("[role=dialog]") === dialog
      && document.activeElement !== dialog && !document.activeElement.disabled) return;

    const target = dialog.querySelector("button:not(:disabled), textarea:not(:disabled)") || dialog;

    target.focus();
  }, []);

  const handleBackButtonClick = useCallback(() => {
    setChoosing(false);
    setError(false);
  }, []);

  const handleSubmit = useCallback(async(event, action) => {
    if (submittingRef.current || !context) return;
    if (action === CancellationActions.RESUME) {
      if (!checkResumeAvailable(context)) {
        setContext({ ...context, resumeAvailable: false });

        return;
      }
    } else {
      if (!context.available || context.cancellationScheduledAt || context.subscriptionStatus === "canceled") return;

      const currentContext = getCancellationContext(context);

      if (currentContext !== context) {
        setContext(currentContext);
        setChoosing(false);
        setError(false);

        return;
      }
      if (!context.retentionActive && (!hasFeedback || (action !== CancellationActions.FEEDBACK && !feedbackRequestId))) return;
      if (action === CancellationActions.RETAIN && !context.offerAvailable) return;
      if (action === CancellationActions.FEEDBACK && context.retentionActive) return;
    }
    submittingRef.current = true;
    setPendingAction(action);
    setError(false);
    if (!requestIdsRef.current[action]) requestIdsRef.current[action] = uuid();

    const response = await dispatch(StripeActions.submitCancellation({
      action,
      feedback: action === CancellationActions.RESUME || context.retentionActive ? undefined : feedback,
      feedbackRequestId: action === CancellationActions.RESUME ? undefined : (feedbackRequestId || undefined),
      requestId: requestIdsRef.current[action],
      organizationId: activeOrganization.id,
      subscriptionId: context.subscriptionId
    }));

    if (!mountedRef.current) return;
    setPendingAction(null);
    setError(!response);
    if (!response) return;
    if (action === CancellationActions.FEEDBACK) {
      if (response.outcome !== CancellationOutcomes.FEEDBACK_SAVED) {
        setError(true);

        return;
      }
      setFeedbackRequestId(requestIdsRef.current[CancellationActions.FEEDBACK]);
      setChoosing(true);

      return;
    }
    setResult(response);
  }, [
    activeOrganization, context, feedback, feedbackRequestId, hasFeedback, dispatch, getCancellationContext, checkResumeAvailable
  ]);

  useEffect(() => {
    const trigger = triggerRef.current;

    mountedRef.current = true;
    feedbackInputRef.current?.focus();
    handleModalShow();

    return () => {
      mountedRef.current = false;
      if (trigger?.disabled) {
        trigger.parentElement?.querySelector("button:not(:disabled)")?.focus();
      } else {
        trigger?.focus();
      }
    };
  }, [handleModalShow]);
  useEffect(() => {
    handleModalShow();
  }, [context, choosing, pendingAction, result, handleModalShow]);

  const offer = context?.offer;

  const offerTextVars = offer ? {
    percentOff: offer.percentOff,
    duration: `${offer.durationInMonths} ${(offer.durationInMonths === 1 ? uiTexts.month : uiTexts.months).toLowerCase()}`
  } : {};

  const discountTitle = offer
    ? Texts.replaceVars(messages.subscriptionDiscountActiveTitle, offerTextVars)
    : messages.subscriptionDiscountActive;

  const subscriptionCanceled = context?.subscriptionStatus === "canceled";

  const modalTitle = subscriptionCanceled ? uiTexts.subscriptionCanceled
    : (context?.cancellationScheduledAt ? uiTexts.manageCancellation : uiTexts.cancelSubscription);

  const resumeAvailable = checkResumeAvailable(context);

  const cancellationDate = result?.effectiveAt || context?.cancellationScheduledAt || context?.currentPeriodEndsAt;

  const cancellationText = Texts.replaceVars(messages.cancellationConfirmedDescription, {
    date: cancellationDate ? moment(cancellationDate).format(DateTimeFormats.DATE_TEXT_EXT) : ""
  });

  const retentionText = context?.retentionDiscountEndsAt
    ? Texts.replaceVars(messages.subscriptionDiscountActiveDescription, {
      date: moment(context.retentionDiscountEndsAt).format(DateTimeFormats.DATE_TEXT_EXT),
      days: Math.max(0, Math.ceil(moment(context.retentionDiscountEndsAt).diff(moment(), "days", true)))
    })
    : messages.subscriptionDiscountActiveDescriptionNoDate;

  return (
    <Modal
      title={modalTitle}
      iconComponent={Icons.XCircle}
      size={result || subscriptionCanceled || context?.cancellationScheduledAt || context?.retentionActive || !context?.available
        ? undefined : "md"}
      dialogClassName={Css.cancelSubscriptionModal}
      loading={!!pendingAction}
      notClosable={!!pendingAction}
      role="dialog"
      tabIndex={-1}
      aria-modal="true"
      aria-label={modalTitle}
      onShow={handleModalShow}
      onClose={handleClose}>
      <div
        ref={contentRef}
        className={!result && context?.available && !context.cancellationScheduledAt && !context.retentionActive
          && (!choosing || context.offerAvailable)
          ? Css.steps : undefined}
        aria-busy={!!pendingAction}>
        {!result && (
          <>
            {subscriptionCanceled && <p>{messages.subscriptionAlreadyCanceled}</p>}
            {!subscriptionCanceled && context?.cancellationScheduledAt && (
              <>
                <div className={Css.stepTitle}>{uiTexts.cancellationScheduled}</div>
                <p>{Texts.replaceVars(moment(context.cancellationScheduledAt).isAfter()
                  ? messages.cancellationScheduledDescription : messages.cancellationDatePassed, {
                  date: moment(context.cancellationScheduledAt).format(DateTimeFormats.DATE_TEXT_EXT)
                })}</p>
                <p>{resumeAvailable ? messages.cancellationWithdrawPrompt : messages.cancellationWithdrawUnavailable}</p>
              </>
            )}
            {!subscriptionCanceled && !context?.cancellationScheduledAt && (context?.available
              ? (
                <>
                  <div className={Css.stepTitle}>
                    {context.retentionActive
                      ? discountTitle
                      : (choosing ? messages.cancellationDecisionStepTitle : messages.cancellationFeedbackStepTitle)}
                  </div>
                  {context.retentionActive
                    ? <p>{retentionText}</p>
                    : (
                      <>
                        {!choosing && (
                          <>
                            <p>{messages.cancellationFeedbackIntroduction}</p>
                            {context.offerAvailable && <p>{messages.cancellationBonusIntroduction}</p>}
                            <label className={Css.feedbackLabel} htmlFor="cancellationFeedback">
                              {messages.cancellationFeedbackLabel}
                            </label>
                            <Input
                              multiline
                              autoFocus
                              inputRef={feedbackInputRef}
                              id="cancellationFeedback"
                              className={Css.feedback}
                              value={feedback}
                              rows={8}
                              resize={false}
                              maxLength={FEEDBACK_MAX_LENGTH}
                              required
                              aria-describedby="cancellationFeedbackHelp"
                              disabled={!!pendingAction}
                              placeholder={messages.cancellationFeedbackPlaceholder}
                              onChange={setFeedback} />
                            <div className={Css.note} id="cancellationFeedbackHelp">{messages.cancellationFeedbackRequired}</div>
                          </>
                        )}
                        {choosing && (
                          <>
                            {feedbackRequestId && <p className={Css.note}>{messages.feedbackSent}</p>}
                            {context.offerAvailable && (
                              <div className={Css.offer}>
                                <div className={Css.offerTitle}>
                                  {Texts.replaceVars(messages.cancellationOfferTitle, offerTextVars)}
                                </div>
                                <p>{Texts.replaceVars(messages.cancellationOfferDescription, offerTextVars)}</p>
                                <p>{messages.cancellationOfferReviewDescription}</p>
                              </div>
                            )}
                          </>
                        )}
                      </>
                    )}
                  {(choosing || context.retentionActive) && <p>{Texts.replaceVars(messages.cancellationDescription, {
                    date: moment(context.currentPeriodEndsAt).format(DateTimeFormats.DATE_TEXT_EXT)
                  })}</p>}
                </>
              )
              : <p>{messages.subscriptionCancellationUnavailable}</p>)}
          </>
        )}
        {result && (
          <div role="status">
            <div className={Css.resultTitle}>
              {result.outcome === CancellationOutcomes.SUBSCRIPTION_RESUMED
                ? uiTexts.cancellationWithdrawn
                : (result.outcome === CancellationOutcomes.DISCOUNT_APPLIED ? discountTitle : uiTexts.cancellationConfirmed)}
            </div>
            {result.outcome === CancellationOutcomes.SUBSCRIPTION_RESUMED
              ? <p>{messages.cancellationWithdrawnDescription}</p>
              : <p>{result.outcome === CancellationOutcomes.DISCOUNT_APPLIED
                ? (result.discountEndsAt
                  ? Texts.replaceVars(messages.subscriptionDiscountAppliedDescription, {
                    date: moment(result.discountEndsAt).format(DateTimeFormats.DATE_TEXT_EXT)
                  })
                  : messages.subscriptionDiscountAppliedDescriptionNoDate)
                : (result.outcome === CancellationOutcomes.CANCELED
                  ? messages.subscriptionAlreadyCanceled : cancellationText)}</p>}
            {feedback && <p>{messages.cancellationFeedbackThanks}</p>}
          </div>
        )}
        {error && (
          <p className={Css.error} role="alert">
            {context?.cancellationScheduledAt ? errors.whileResumingSubscription : errors.whileSubmittingCancellation}
          </p>
        )}
      </div>
      <ModalFooter className={Css.footer}>
        {result || subscriptionCanceled || !context || (!context.available && !context.cancellationScheduledAt)
          ? <Button large primary onClick={handleClose}>{uiTexts.close}</Button>
          : (context.cancellationScheduledAt
            ? (
              <>
                <Button large disabled={!!pendingAction} onClick={handleClose}>{uiTexts.close}</Button>
                {resumeAvailable && (
                  <Button
                    large primary
                    value={CancellationActions.RESUME}
                    loading={pendingAction === CancellationActions.RESUME}
                    disabled={!!pendingAction}
                    onClick={handleSubmit}>
                    {uiTexts.keepSubscription}
                  </Button>
                )}
              </>
            )
            : (
              <>
                <Button large disabled={!!pendingAction} onClick={choosing ? handleBackButtonClick : handleClose}>
                  {choosing ? uiTexts.back : uiTexts.keepSubscription}
                </Button>
                {!context.retentionActive && !choosing && (
                  <Button
                    large primary
                    value={CancellationActions.FEEDBACK}
                    loading={pendingAction === CancellationActions.FEEDBACK}
                    disabled={!!pendingAction || !hasFeedback}
                    onClick={handleSubmit}>
                    {uiTexts.submitFeedbackAndContinue}
                  </Button>
                )}
                {choosing && context.offerAvailable && (
                  <Button
                    large primary
                    value={CancellationActions.RETAIN}
                    loading={pendingAction === CancellationActions.RETAIN}
                    disabled={!!pendingAction}
                    onClick={handleSubmit}>
                    {Texts.replaceVars(messages.cancellationAcceptOffer, offerTextVars)}
                  </Button>
                )}
                {(choosing || context.retentionActive) && (
                  <Button
                    large danger outline
                    value={CancellationActions.CANCEL}
                    loading={pendingAction === CancellationActions.CANCEL}
                    disabled={!!pendingAction}
                    onClick={handleSubmit}>
                    {uiTexts.cancelAtPeriodEnd}
                  </Button>
                )}
              </>
            ))}
      </ModalFooter>
    </Modal>
  );
};

export default React.memo(CancelSubscriptionModal);
